// FIX-D300-S2 (D-300 sec.2-5) - "לקוח בלי כיסוי": pure helpers shared by the ticket page banner, the request
// dialog, the shared 409 handler and the settings tab (switch + report).

export const COVERAGE_SETTING_CATEGORY = 'coverage';
export const COVERAGE_BLOCK_KEY = 'block_work_enabled';
export const CUSTOMER_NO_COVERAGE = 'CUSTOMER_NO_COVERAGE';

export type CoverageApprovalScope = 'ticket' | 'customer';

export interface CoverageRequestInfo {
  id: string;
  status: 'pending' | 'approved' | 'resolved_bank' | 'resolved_contract' | 'cancelled' | string;
  approval_scope?: CoverageApprovalScope | null;
  message_extra?: string | null;
  requested_by?: string | null;
  requested_by_name?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  created_at: string;
}

/** GET /tickets/:id `coverage` (staff, service requests only). */
export interface TicketCoverageInfo {
  applies: boolean;
  covered: boolean;
  block_enabled: boolean;
  blocked: boolean;
  warranty?: boolean;
  service?: boolean;
  approval?: CoverageApprovalScope | null;
  organization_id: string | null;
  organization_name: string | null;
  ticket_title: string | null;
  request: CoverageRequestInfo | null;
  can_decide?: boolean;
}

/** A row of the report "לקוחות בלי כיסוי" (GET /coverage/uncovered). */
export interface UncoveredCustomer {
  organization_id: string;
  organization_name: string;
  account_manager_name: string | null;
  open_tickets: number;
  pending_requests: number;
  /** A customer-wide "בלי חיוב" approval is in force: not blocked. */
  approved: boolean;
}

/** The details of a 409 CUSTOMER_NO_COVERAGE. */
export interface CoverageBlockedDetails {
  ticket_id: string;
  organization_id?: string | null;
  organization_name?: string | null;
  ticket_title?: string | null;
  request?: { id: string; status: string; created_at: string } | null;
}

/** The setting value: only 'true' is on (missing / anything else = off, the default). */
export function isCoverageBlockOn(value: string | null | undefined): boolean {
  return String(value ?? '').trim().toLowerCase() === 'true';
}

export function coverageSettingValue(on: boolean): string {
  return on ? 'true' : 'false';
}

/** The details when `err` is a 409 CUSTOMER_NO_COVERAGE (the API client's error shape), else null. */
export function coverageBlockedDetails(err: unknown): CoverageBlockedDetails | null {
  const e = err as { error?: { error_code?: string; details?: Partial<CoverageBlockedDetails> } } | null;
  if (e?.error?.error_code !== CUSTOMER_NO_COVERAGE) return null;
  const d = e.error.details ?? {};
  if (typeof d.ticket_id !== 'string' || !d.ticket_id) return null;
  return {
    ticket_id: d.ticket_id,
    organization_id: d.organization_id ?? null,
    organization_name: d.organization_name ?? null,
    ticket_title: d.ticket_title ?? null,
    request: d.request ?? null,
  };
}

/** What the ticket page shows for a service request (staff). */
export type CoverageBannerMode =
  | 'none'
  /** The switch is on: work is blocked; the only action is "שליחת הודעה למנהל" (or "ממתין"). */
  | 'blocked'
  /** The switch is off: nothing is blocked; "שליחת הודעה למנהל" is offered as a request for permission. */
  | 'notice';

export function coverageBannerMode(cov: TicketCoverageInfo | null | undefined): CoverageBannerMode {
  if (!cov || !cov.applies || cov.covered) return 'none';
  return cov.block_enabled ? 'blocked' : 'notice';
}

/** A pending request of the ticket ("נשלחה הודעה למנהל ב-…, ממתין"). */
export function pendingRequest(cov: TicketCoverageInfo | null | undefined): CoverageRequestInfo | null {
  return cov?.request && cov.request.status === 'pending' ? cov.request : null;
}

/** The manager sees the 3 buttons: a pending request and the right to decide it. */
export function showDecisionButtons(cov: TicketCoverageInfo | null | undefined): boolean {
  return coverageBannerMode(cov) !== 'none' && !!pendingRequest(cov) && cov?.can_decide === true;
}

/** The engagement form the manager's [הוספת בנק שעות] / [הוספת חוזה שירות] opens (the customer's page). */
export function newEngagementPath(organizationId: string, type: 'hour_bank' | 'service_contract'): string {
  return `/organizations/${organizationId}?new_engagement=${type}`;
}
